import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiCloudLine } from '@remixicon/react';
import {
  ExternalLink,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const RedisCloudPSCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  const subscriptionId = annotations['redis-cloud.infrastructure/subscription-id'];
  const redisRegion = annotations['redis-cloud.infrastructure/redis-region'];
  const pscServiceId = annotations['redis-cloud-psc.infrastructure/service-id'];
  const pscEndpointId = annotations['redis-cloud-psc.infrastructure/endpoint-id'];
  const pscIpAddresses = annotations['redis-cloud-psc.infrastructure/ip-addresses'];
  const pscForwardingRules = annotations['redis-cloud-psc.infrastructure/forwarding-rules'];
  const pscDnsRecords = annotations['redis-cloud-psc.infrastructure/dns-records'];
  const gcpProjectId = annotations['gcp.infrastructure/project-id'];
  const gcpNetworkName = annotations['redis-cloud-psc.infrastructure/network-name'];
  const gcpSubnetName = annotations['redis-cloud-psc.infrastructure/subnet-name'];

  const hasRedisCloudPSCInfo = pscServiceId || pscEndpointId;

  if (!hasRedisCloudPSCInfo) {
    return null;
  }

  const ipAddresses = pscIpAddresses ? pscIpAddresses.split(',') : [];
  const forwardingRules = pscForwardingRules ? pscForwardingRules.split(',') : [];
  const dnsRecords = pscDnsRecords ? pscDnsRecords.split(',') : [];

  return (
    <InfoCard
      title="Redis Cloud Private Service Connect Information"
      icon={<RiCloudLine size={20} />}
    >
      <InfraStack>
        <InfraSection title="☁️ Redis Cloud Subscription">
          {subscriptionId && (
            <InfraField label="Subscription ID:" value={subscriptionId} />
          )}
          {redisRegion && (
            <InfraField
              label="Region:"
              value={<InfraTagValue>{redisRegion}</InfraTagValue>}
              mono={false}
            />
          )}
          {pscServiceId && (
            <InfraField label="PSC Service ID:" value={pscServiceId} />
          )}
        </InfraSection>

        <InfraSection title="🔗 GCP VPC">
          {gcpProjectId && (
            <InfraField label="Project ID:" value={gcpProjectId} />
          )}
          {gcpNetworkName && (
            <InfraField
              label="Network:"
              value={<InfraTagValue>{gcpNetworkName}</InfraTagValue>}
              mono={false}
            />
          )}
          {gcpSubnetName && (
            <InfraField
              label="Subnet:"
              value={<InfraTagValue>{gcpSubnetName}</InfraTagValue>}
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🔄 Private Service Connect Details">
          {pscEndpointId && (
            <InfraField label="Endpoint ID:" value={pscEndpointId} />
          )}
          {ipAddresses.length > 0 && (
            <InfraField
              label="Internal IP Addresses:"
              value={
                <InfraTagRow>
                  {ipAddresses.map((ip, index) => (
                    <InfraTag key={index}>{ip}</InfraTag>
                  ))}
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {forwardingRules.length > 0 && (
            <InfraField
              label="Forwarding Rules:"
              value={
                <InfraTagRow>
                  {forwardingRules.map((rule, index) => (
                    <InfraTag key={index}>{rule}</InfraTag>
                  ))}
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {dnsRecords.length > 0 && (
            <InfraField
              label="DNS Records:"
              value={
                <InfraTagRow>
                  {dnsRecords.map((dns, index) => (
                    <InfraTag key={index}>{dns}</InfraTag>
                  ))}
                </InfraTagRow>
              }
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🚀 Quick Start">
          <Text variant="body-medium">
            Your Private Service Connect endpoint is established! You can now access Redis
            Cloud databases from your GCP VPC.
          </Text>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            Test connectivity from a Compute Engine instance in your VPC:
          </Text>
          <InfraCodeBlock variant="light">
            {`# Install redis-cli
sudo apt-get update && sudo apt-get install -y redis-tools

# Test connection (replace with your database endpoint from Redis Cloud Console)
redis-cli -h <database-endpoint> -p <port> PING

# Expected output: PONG`}
          </InfraCodeBlock>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            Next Steps:
          </Text>
          <ul>
            <li>
              <Text variant="body-medium">
                Get database endpoint from{' '}
                <ExternalLink href="https://app.redislabs.com">
                  Redis Cloud Console
                </ExternalLink>
              </Text>
            </li>
            <li>
              <Text variant="body-medium">
                Ensure your Compute Engine instances are in the same VPC:{' '}
                <code>{gcpNetworkName || 'N/A'}</code>
              </Text>
            </li>
            <li>
              <Text variant="body-medium">
                Verify DNS resolution is working for Redis Cloud endpoints
              </Text>
            </li>
            <li>
              <Text variant="body-medium">
                Check firewall rules allow traffic to Redis ports (6379, 10000-19999)
              </Text>
            </li>
          </ul>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            📚 Documentation:
          </Text>
          <ul>
            <li>
              <Text variant="body-medium">
                <ExternalLink href="https://docs.redis.com/latest/rc/security/private-service-connect/">
                  Redis Cloud Private Service Connect Guide
                </ExternalLink>
              </Text>
            </li>
            <li>
              <Text variant="body-medium">
                <ExternalLink href="https://cloud.google.com/vpc/docs/private-service-connect">
                  GCP Private Service Connect Documentation
                </ExternalLink>
              </Text>
            </li>
          </ul>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
